import type {
  NotificationApiCondition,
  NotificationApiConditions,
  NotificationApiSchedule,
  WatchlistSubscriptionRecord,
} from '@circuitry-ai/doc-data';

export interface WatchlistAddedBySelf {
  type: 'self';
}

export interface WatchlistAddedByAdmin {
  type: 'admin';
  name: string;
  role: string;
}

export type WatchlistAddedBy = WatchlistAddedBySelf | WatchlistAddedByAdmin;

export interface WatchlistSubscriptionView {
  id: string;
  name: string;
  description: string;
  frequency: string;
  deliveryTimeLabel: string;
  addedBy: WatchlistAddedBy;
  isActive: boolean;
}

const FREQUENCY_LABELS: Record<string, string> = {
  immediate: 'Immediately',
  daily: 'Daily',
  weekly: 'Weekly summary',
  monthly: 'Monthly',
  one_time: 'One time',
};

const WEEKDAY_LABELS: Record<string, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

function toHour12(hour24: number): number {
  if (hour24 === 0) {
    return 12;
  }
  if (hour24 > 12) {
    return hour24 - 12;
  }
  return hour24;
}

function formatTime12h(time24h: string): string {
  const [hours, minutes = '00'] = time24h.split(':');
  const hour24 = Number.parseInt(hours, 10);
  if (Number.isNaN(hour24)) {
    return time24h;
  }
  const hour12 = toHour12(hour24);
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
}

function formatTimezoneAbbrev(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function formatConditionValue(condition: NotificationApiCondition): string {
  return Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value);
}

function formatConditionSummary(condition: NotificationApiCondition): string {
  const label = condition.field;
  const value = formatConditionValue(condition);

  switch (condition.operator) {
    case 'equals':
      return `${label} is ${value}`;
    case 'not_equals':
      return `${label} is not ${value}`;
    case 'is_one_of':
      return `${label} is ${value}`;
    case 'is_not_one_of':
      return `${label} is not ${value}`;
    default:
      return `${label}: ${value}`;
  }
}

function summarizeConditions(conditions: NotificationApiConditions | undefined): string {
  const rules = conditions?.conditions ?? [];
  if (rules.length === 0) {
    return 'All matching records';
  }
  return rules.map((condition) => formatConditionSummary(condition)).join('; ');
}

export function formatWatchlistFrequency(frequency: string): string {
  return FREQUENCY_LABELS[frequency] ?? frequency;
}

export function formatWatchlistDeliveryTime(
  frequency: string,
  schedule: NotificationApiSchedule | undefined
): string {
  if (frequency === 'immediate') {
    return 'Immediately';
  }
  if (!schedule?.time) {
    return '—';
  }

  const timeLabel = formatTime12h(schedule.time);
  const timezoneLabel = formatTimezoneAbbrev(schedule.timezone ?? 'UTC');

  if (frequency === 'daily') {
    return `Daily, ${timeLabel} ${timezoneLabel}`;
  }

  if (frequency === 'weekly') {
    const days = (schedule.days ?? [])
      .map((day) => WEEKDAY_LABELS[day.toLowerCase()] ?? day)
      .join(', ');
    if (days) {
      return `Every ${days}, ${timeLabel} ${timezoneLabel}`;
    }
  }

  if (frequency === 'monthly') {
    return `Monthly, ${timeLabel} ${timezoneLabel}`;
  }

  if (frequency === 'one_time' && schedule.date) {
    return `${schedule.date} at ${timeLabel} ${timezoneLabel}`;
  }

  return `${timeLabel} ${timezoneLabel}`;
}

function buildWatchlistName(record: WatchlistSubscriptionRecord): string {
  const typeLabel = record.notification_type;
  const frequencyLabel = formatWatchlistFrequency(record.frequency).toLowerCase();
  return `${typeLabel} — ${frequencyLabel}`;
}

function resolveAddedBy(
  addedByUserId: string,
  currentUserId: string | undefined
): WatchlistAddedBy {
  if (currentUserId && addedByUserId === currentUserId) {
    return { type: 'self' };
  }
  return { type: 'admin', name: 'Admin', role: 'admin' };
}

export function mapWatchlistRecordToView(
  record: WatchlistSubscriptionRecord,
  currentUserId: string | undefined
): WatchlistSubscriptionView {
  return {
    id: record.config_id,
    name: buildWatchlistName(record),
    description: summarizeConditions(record.conditions),
    frequency: record.frequency,
    deliveryTimeLabel: formatWatchlistDeliveryTime(record.frequency, record.schedule),
    addedBy: resolveAddedBy(record.added_by, currentUserId),
    isActive: record.state === 'active',
  };
}

export function mapWatchlistRecordsToViews(
  records: WatchlistSubscriptionRecord[],
  currentUserId: string | undefined
): WatchlistSubscriptionView[] {
  const views: WatchlistSubscriptionView[] = [];
  for (const record of records) {
    views.push(mapWatchlistRecordToView(record, currentUserId));
  }
  return views;
}

function getErrorStatusCode(error: object): number | undefined {
  if ('status_code' in error && typeof error.status_code === 'number') {
    return error.status_code;
  }
  if ('originalStatus' in error) {
    const originalStatus = (error as { originalStatus?: number }).originalStatus;
    if (typeof originalStatus === 'number') {
      return originalStatus;
    }
  }
  if ('status' in error) {
    const status = (error as { status?: number }).status;
    if (typeof status === 'number') {
      return status;
    }
  }
  return undefined;
}

export function isWatchlistNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return getErrorStatusCode(error) === 404;
}
